<template>
  <div :class="$setClasses">
    <nxt-core-next-transition :visible="auto">
      <nxt-core-input-label v-if="label" :text="label" />
      <div>
        <nxt-core-input-address
          v-if="!displayCountry"
          v-model:address="addressSync"
          :country="modelCountry"
          :input-id="inputId"
          :label="label"
          :placeholder="placeholder || ''"
        />
        <nxt-core-input-country
          v-else
          v-model="modelCountry"
          :country="countrySync"
          :input-id="`${inputId}-country`"
        >
          <slot>
            <nxt-core-input-address
              v-model:address="addressSync"
              :country="modelCountry"
              :input-id="inputId"
              :label="label"
              :placeholder="placeholder || ''"
            />
          </slot>
        </nxt-core-input-country>
      </div>
    </nxt-core-next-transition>
    <nxt-core-next-transition :visible="displayDecomposedForm">
      <div :class="$vClass('decomposed')">
        <nxt-core-input-label :input-id="`${inputId}-street`" />
        <el-row :gutter="25">
          <vee-field
            v-if="inputStreet?.display || displayStreet"
            v-slot="{ meta, errors: errorsField }"
            :label="inputStreet.label.toLowerCase()"
            :modelValue="streetSync"
            :name="`${inputId}-street`"
            :rules="fieldRules(inputStreet)"
            standalone
            ref="provider-street"
          >
            <el-col
              :class="
                generateClasses(meta, [
                  $vClass('decomposed__item'),
                  $vClass('decomposed__item__street'),
                ])
              "
              :lg="10"
              :md="24"
              :sm="9"
              :xs="24"
            >
              <nxt-core-input-with-label
                v-model="streetSync"
                :input-id="`${inputId}-street`"
                :label="inputStreet.label"
                :placeholder="inputStreet.placeholder"
                autocomplete="street-address"
                class="input__value"
                @focus="handlerGenerateError(inputStreet)"
              />
              <span v-if="showErrors(errorsField)" :class="$vClass('decomposed__errors')">
                {{ errorsField[0] }}
              </span>
            </el-col>
          </vee-field>
          <vee-field
            v-slot="{ meta, errors: errorsField }"
            :label="inputPostal.label.toLowerCase()"
            :modelValue="postalSync"
            :name="`${inputId}-postal`"
            :rules="fieldRules(inputPostal)"
            ref="provider-postal"
            standalone
          >
            <el-col
              :class="
                generateClasses(meta, [
                  $vClass('decomposed__item'),
                  $vClass('decomposed__item__cp'),
                ])
              "
              :lg="5"
              :md="10"
              :sm="5"
              :xs="24"
            >
              <nxt-core-input-with-label
                v-model="postalSync"
                :input-id="`${inputId}-postal`"
                :label="inputPostal.label"
                :placeholder="inputPostal.placeholder"
                autocomplete="postal-code"
                class="input__value"
                data-cy="address-postal"
                @focus="handlerGenerateError(inputPostal)"
              />
              <span v-if="showErrors(errorsField)" :class="$vClass('decomposed__errors')">
                {{ errorsField[0] }}
              </span>
            </el-col>
          </vee-field>
          <el-col
            :class="[
              $vClass('decomposed__item'),
              $vClass('decomposed__item__city'),
              inputCountry?.display && '--with-country',
            ]"
            :lg="9"
            :md="14"
            :sm="10"
            :xs="24"
          >
            <div class="u-display-flex">
              <vee-field
                v-slot="{ meta, errors: errorsField }"
                :label="inputCity.label.toLowerCase()"
                :modelValue="citySync"
                :name="`${inputId}-city`"
                :rules="fieldRules(inputCity)"
                ref="provider-city"
                standalone
              >
                <div class="u-width-100">
                  <nxt-core-input-with-label
                    v-model="citySync"
                    :class="generateClasses(meta, ['input__value'])"
                    :input-id="`${inputId}-city`"
                    :label="inputCity.label"
                    :placeholder="inputCity.placeholder"
                    autocomplete="address-level2"
                    data-cy="address-city"
                    @focus="handlerGenerateError(inputCity)"
                  />
                  <span
                    v-if="showErrors(errorsField)"
                    :class="['u-position-absolute u-margin-top-0', $vClass('decomposed__errors')]"
                    >{{ errorsField[0] }}</span
                  >
                </div>
              </vee-field>
              <vee-field
                v-if="inputCountry?.display"
                v-slot="{ errors: errorsField }"
                :modelValue="countrySync"
                :name="`${inputId}-country`"
                :rules="fieldRules(inputCountry)"
                ref="provider-country"
                standalone
              >
                <div>
                  <nxt-core-input-country
                    v-if="inputCountry?.display"
                    v-model="countrySync"
                    :country="countrySync"
                    :input-id="`${inputId}-country`"
                    @update:modelValue="() => {
                      handlerGenerateError(inputCountry)
                      handlerGenerateError(inputPostal)
                    }"
                  ></nxt-core-input-country>
                  <span
                    v-if="showErrors(errorsField)"
                    :class="['u-position-absolute u-margin-top-0', $vClass('decomposed__errors')]"
                  >
                    {{ errorsField[0] }}</span
                  >
                </div>
              </vee-field>
            </div>
          </el-col>
        </el-row>
      </div>
    </nxt-core-next-transition>
  </div>
</template>

<script>
import AutoCompleteAddress from "@nextories/vue-core-components/src/components/molecules/address/NxtCoreAutoCompleteAddress.vue";

export default {
  extends: AutoCompleteAddress,
  name: "AutoCompleteAddress",
  mounted() {
    this.$nextTick(() => {
      // Display errors
      if (this.displayDecomposedForm && this.displayErrors && this.$refs) {
        for (const input of this.listInput) {
          if (input) {
            // Call vee-valide
            if (this.$refs[`provider-${input.provider}`]) {
              this.$refs[`provider-${input.provider}`].validate(this[input.type]);
            }
            // Generate errors of input
            this.handlerGenerateError(input);
          }
        }
      }
    });
  },
  methods: {
    generateClasses(meta, classes = []) {
      return classes.concat([
        meta.validated && meta.valid && "is-valid",
        meta.validated && this.displayErrors && !meta.valid && "is-invalid is-error",
      ]);
    },
    // Use input.rules when possible; for postal, also apply regex (address-postal
    // expects an Address object, but the field model is a string).
    fieldRules(input) {
      if (!input) return undefined;
      if (input.regex) {
        const rules = {};
        if (input.required) rules.required = true;
        rules.regex = input.regex;
        return rules;
      }
      return input.rules;
    },
  },
  watch: {
    displayErrors(value) {
      if (value) {
        for (const input of this.listInput) {
          if (input && this.$refs[`provider-${input.provider}`]) {
            this.$refs[`provider-${input.provider}`].validate(this[input.type]);
          }
          this.handlerGenerateError(input);
        }
      }
    },
  },
};
</script>

<style lang="scss">
@use "@nextories/vue-core-components/src/assets/scss/components/molecules/address/NxtCoreAutoCompleteAddress" with ($prefixName: $prefixName);
</style>
